import { create } from "zustand";
import { ModalType } from "../../user-invitation-modal/api/types";
import { UserSchema } from "./schema";
import { cropModel, initialUserData } from "./types";
import { UserModel } from "../../../../main/users-facility/api/types";
import { UserRoles } from "../../../enums/user-roles";
import { createRef } from "react";

export interface IUserInvState {
  isModalOpen: boolean;
  pageSize: number;
  pageNumber: number;
  modalType: ModalType;
  queryData: UserSchema;
  selectedUser: UserModel | undefined;
  clearingUserForm: boolean;
  currentStep: number;
  originalFileName: string;
  openCropDialog: boolean;
  defaultRoleValue: UserRoles | undefined;
  zoom: number;
  crop: { x: number; y: number };
  croppedAreaPixels: cropModel | null;
  searchButtonRef: React.RefObject<HTMLButtonElement>;
  selectActiveRecordButtonRef: React.RefObject<HTMLButtonElement>;
  inviteUserFirstResultButtonRef: React.RefObject<HTMLTableRowElement>;

  setDefaultRoleValue: (val: UserRoles | undefined) => void;

  setOpenCropDialog: (val: boolean) => void;
  setOriginalFileName: (val: string) => void;

  setCurrentStep: (val: number) => void;
  setClearingUserForm: (val: boolean) => void;
  setQueryData: (val: UserSchema) => void;
  setSelectedUser: (val: UserModel | undefined) => void;
  setModalType: (val: ModalType) => void;
  setModal: (val: boolean) => void;
  headerTitle: (modalTitle?: string) => string;
  headerSubtitle: (headerSubtitle?: string) => string;
  setPageSize: (val: number) => void;
  setPageNumber: (val: number) => void;

  setCroppedAreaPixels: (val: cropModel | null) => void;
  setCrop: (val: { x: number; y: number }) => void;
  setZoom: (val: number) => void;

  previewUrl: string | null;
  setPreviewUrl: (val: string | null) => void;

  previewUrl2: string | null;
  setPreviewUrl2: (val: string | null) => void;

  walkthroughMode: boolean;
  setWalkthroughMode: (val: boolean) => void;

  isAddingFacilityUser: boolean;
  setIsAddingFacilityUser: (val: boolean) => void;

  isFacilityUsersModule: boolean;
  setIsFacilityUsersModule: (val: boolean) => void;

  isAddingGroupAdmin: boolean;
  setIsAddingGroupAdmin: (val: boolean) => void;

  isAddingBizBoxUser: boolean;
  setIsAddingBizBoxUser: (val: boolean) => void;

  facilityCode: string | undefined;
  setFacilityCode: (val: string | undefined) => void;

  productCode: string | undefined;
  setProductCode: (val: string | undefined) => void;

  resetAddingUser: () => void;
}

const useUserInvStore = create<IUserInvState>((set, get) => ({
  isModalOpen: false,
  pageSize: 10,
  pageNumber: 1,
  modalType: ModalType.QueryForm,
  queryData: initialUserData,
  selectedUser: undefined,
  clearingUserForm: false,
  currentStep: 0,
  previewUrl: null,
  previewUrl2: null,
  originalFileName: "",
  openCropDialog: false,
  defaultRoleValue: undefined,

  zoom: 1,
  crop: { x: 0, y: 0 },
  croppedAreaPixels: null,

  searchButtonRef: createRef<HTMLButtonElement>(),
  selectActiveRecordButtonRef: createRef<HTMLButtonElement>(),
  inviteUserFirstResultButtonRef: createRef<HTMLTableRowElement>(),

  setCroppedAreaPixels: (croppedAreaPixels: cropModel | null) =>
    set(() => ({ croppedAreaPixels })),
  setCrop: (crop: { x: number; y: number }) => set(() => ({ crop })),
  setZoom: (zoom: number) => set(() => ({ zoom })),

  setDefaultRoleValue: (defaultRoleValue: UserRoles | undefined) =>
    set(() => ({ defaultRoleValue })),
  setOpenCropDialog: (openCropDialog: boolean) =>
    set(() => ({ openCropDialog })),
  setOriginalFileName: (originalFileName: string) =>
    set(() => ({ originalFileName })),

  setPreviewUrl2: (previewUrl2: string | null) => set(() => ({ previewUrl2 })),
  setPreviewUrl: (previewUrl: string | null) => set(() => ({ previewUrl })),
  setCurrentStep: (currentStep) => set(() => ({ currentStep })),
  setClearingUserForm: (clearingUserForm) => set(() => ({ clearingUserForm })),
  setQueryData: (queryData) => set(() => ({ queryData })),
  setSelectedUser: (selectedUser) => set(() => ({ selectedUser })),
  setModalType: (modalType) => set({ modalType }),
  setModal: (isModalOpen) => set({ isModalOpen }),
  setPageSize: (pageSize: number) => set(() => ({ pageSize })),
  setPageNumber: (pageNumber: number) => set(() => ({ pageNumber })),

  headerTitle: (modalTitle) => {
    if (get().modalType === ModalType.QueryResult) {
      return "Similar Users Found";
    } else if (get().modalType === ModalType.QueryForm) {
      return modalTitle ?? "User Invitation";
    } else {
      return "Unknown";
    }
  },
  headerSubtitle: (headerSubtitle) => {
    if (get().modalType === ModalType.QueryResult) {
      return "Choose to update an existing record or to continue creating a new user.";
    } else if (get().modalType === ModalType.QueryForm) {
      return (
        headerSubtitle ?? "Please fill in the details below to invite a user."
      );
    } else {
      return "Unknown";
    }
  },

  walkthroughMode: false,
  setWalkthroughMode: (walkthroughMode: boolean) =>
    set(() => ({ walkthroughMode })),

  isAddingFacilityUser: false,
  setIsAddingFacilityUser: (val: boolean) =>
    set(() => ({ isAddingFacilityUser: val })),

  isFacilityUsersModule: false,
  setIsFacilityUsersModule: (val: boolean) =>
    set(() => ({ isFacilityUsersModule: val })),

  isAddingGroupAdmin: false,
  setIsAddingGroupAdmin: (val: boolean) =>
    set(() => ({ isAddingGroupAdmin: val })),

  isAddingBizBoxUser: false,
  setIsAddingBizBoxUser: (val: boolean) =>
    set(() => ({ isAddingBizBoxUser: val })),

  facilityCode: undefined,
  setFacilityCode: (val: string | undefined) => set(() => ({ facilityCode: val })),

  productCode: undefined,
  setProductCode: (val: string | undefined) => set(() => ({ productCode: val })),

  resetAddingUser: () =>
    set({
      isAddingBizBoxUser: false,
      isAddingFacilityUser: false,
      isAddingGroupAdmin: false,
      isFacilityUsersModule: false,
      facilityCode: undefined,
      productCode: undefined,
    }),
}));

export default useUserInvStore;
